/**
 * Stylised pump silhouette used as the per-pump status icon in fleet
 * lists (Dashboard.PumpCard). Replaces the previous solid status dot.
 *
 *   ┌──────┐    ⌒
 *   │      │   ╱  ⌒ intake nozzle
 *   │ MTR  ├─[ VOL ]─⊐  discharge
 *   │      │   ╲___╱
 *   └─┬──┬─┘  ┌─┬──┐
 *     ▔▔  ▔▔   ▔  ▔▔  feet
 *
 * Side-view: motor block on the left, volute (circular pump body)
 * on the right, vertical intake pipe rising from the top of the
 * volute, horizontal discharge nub on the right, base feet.
 *
 * Single-colour fill via currentColor so the same SVG flips between
 * online (green) and offline (muted) without duplicating geometry.
 * No stroke — silhouette reads cleanly at 20-32px.
 *
 *   <PumpThumb online={isConnected} />
 *   <PumpThumb online size={32} />
 *
 * Both light and dark theme:
 *   --aqua-online        provides the live green
 *   --aqua-text-muted    provides the greyed-out tone (theme-aware)
 */
export function PumpThumb({
  online,
  size = 28,
}: {
  online: boolean;
  size?: number;
}) {
  const color = online ? 'var(--aqua-online)' : 'var(--aqua-text-muted)';
  const label = online ? 'Pump online' : 'Pump offline';
  return (
    <span
      style={{
        flexShrink: 0,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: size,
        height: size,
        color,
        // Subtle halo glow when online, mirrors the prior dot's
        // box-shadow polish so the live state still feels "alive".
        filter: online
          ? 'drop-shadow(0 0 4px rgba(34, 197, 94, 0.35))'
          : undefined,
      }}
      title={label}
      aria-label={label}
      role="img"
    >
      <svg
        viewBox="0 0 24 24"
        width={size}
        height={size}
        fill="currentColor"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden="true"
      >
        {/* Motor block — left, vertical rounded rect. */}
        <rect x="2" y="8" width="6.5" height="10" rx="1.4" />
        {/* Motor cooling band — small dark line implied via cut-out
            rectangle laid back over the motor. We inset by leaving a
            thin gap, but since we draw single-colour we just keep the
            block solid; the silhouette stays clean. */}
        {/* Volute / pump body — right, circular. */}
        <circle cx="14.5" cy="13" r="5.2" />
        {/* Coupling between motor and volute (short stub). */}
        <rect x="8" y="11.5" width="2" height="3" />
        {/* Intake — vertical pipe rising from the top of the volute. */}
        <rect x="12.6" y="3" width="3.8" height="6" rx="0.6" />
        {/* Discharge — short horizontal nub on the right of the volute. */}
        <rect x="19.4" y="11.5" width="3" height="3" rx="0.5" />
        {/* Feet — two short rounded rects at the bottom for stability. */}
        <rect x="2.6" y="18.6" width="5.5" height="1.8" rx="0.6" />
        <rect x="11.5" y="18.4" width="6.5" height="2" rx="0.6" />
      </svg>
    </span>
  );
}
